import { useCallback, useEffect, useState, type FormEvent } from 'react';
import {
  ActionsLigne, Bouton, Carte, Champ, EnTetePage, Etiquette, GrilleFormulaire,
  GroupeBoutons, IconeCorbeille, IconeCrayon, IconeCroix, IconeEquipe, Message,
  Selecteur,
  SqueletteTableau,
  Tableau, Vide, useRetours,
} from '@journal/ui';
import { messageErreur, useSession } from '../lib/session.tsx';
import { useFonctionnalite } from '../lib/droits.tsx';
import { DialogueNomMembre } from '../components/DialogueNomMembre.tsx';
import { GestionClasses } from '../components/GestionClasses.tsx';
import { LienBouton } from '../components/LienBouton.tsx';
import type {
  InvitationEnAttente, Membre, UtilisateurCourant,
} from '../lib/api.ts';

/**
 * Gestion de l'équipe.
 *
 * Deux garde-fous portent toute la sûreté de cet écran, et ils doublent des
 * règles serveur — jamais ils ne les remplacent :
 *
 * 1. On ne propose pas d'action qu'on n'a pas le droit d'exécuter. Un bouton
 *    qui répond 403 use la confiance dans l'interface.
 * 2. On ne propose pas de se retirer soi-même, ni de retirer le dernier
 *    propriétaire : l'école se retrouverait sans personne pour gérer son
 *    abonnement.
 */

const LIBELLE_ROLE: Record<Membre['role'], string> = {
  OWNER: 'Propriétaire',
  ADMIN: 'Administrateur',
  MEMBER: 'Enseignant',
};

const RANG: Record<Membre['role'], number> = { MEMBER: 1, ADMIN: 2, OWNER: 3 };

const ROLES_ASSIGNABLES: Membre['role'][] = ['MEMBER', 'ADMIN', 'OWNER'];

export function Equipe() {
  const { api } = useSession();
  const { notifier, confirmer } = useRetours();
  const equipeIncluse = useFonctionnalite('equipe');

  const [moi, setMoi] = useState<UtilisateurCourant | null>(null);
  const [membres, setMembres] = useState<Membre[] | null>(null);
  const [invitations, setInvitations] = useState<InvitationEnAttente[]>([]);
  const [erreur, setErreur] = useState<string | null>(null);

  const [emailInvite, setEmailInvite] = useState('');
  const [prenomInvite, setPrenomInvite] = useState('');
  const [nomInvite, setNomInvite] = useState('');
  const [roleInvite, setRoleInvite] = useState<Membre['role']>('MEMBER');
  const [envoiEnCours, setEnvoiEnCours] = useState(false);
  const [mode, setMode] = useState<'creation' | 'invitation'>('creation');
  /** Membre dont on corrige la fiche. `null` = le dialogue est fermé. */
  const [renomme, setRenomme] = useState<Membre | null>(null);

  const recharger = useCallback(async () => {
    try {
      const utilisateur = await api.requete<UtilisateurCourant>('/auth/me');
      setMoi(utilisateur);
      setMembres(await api.requete<Membre[]>('/tenant/members'));
      // Les invitations en attente sont réservées aux administrateurs :
      // les demander en tant qu'enseignant produirait un 403 inutile.
      if (RANG[utilisateur.role] >= RANG.ADMIN) {
        setInvitations(await api.requete<InvitationEnAttente[]>('/tenant/invitations'));
      }
    } catch (e) {
      setErreur(messageErreur(e));
    }
  }, [api]);

  useEffect(() => { void recharger(); }, [recharger]);

  async function inviter(evenement: FormEvent) {
    evenement.preventDefault();
    setErreur(null);
    setEnvoiEnCours(true);
    try {
      // La route diffère, la charge utile non : c'est la même décision, prise
      // avec ou sans l'accord préalable de l'intéressé.
      /*
       * Le nom n'accompagne QUE la création.
       *
       * Une invitation ne crée pas le compte : c'est l'intéressé qui le fait
       * en l'acceptant, et c'est là qu'il donne son nom. Le saisir ici serait
       * le nommer à sa place, pour une identité qui sera écrasée.
       */
      await api.requete(
        mode === 'creation' ? '/tenant/members' : '/tenant/invitations',
        {
          methode: 'POST',
          corps: mode === 'creation'
            ? {
              email: emailInvite, role: roleInvite,
              firstName: prenomInvite.trim(), lastName: nomInvite.trim(),
            }
            : { email: emailInvite, role: roleInvite },
        },
      );
      notifier(
        mode === 'creation'
          ? `Compte créé pour ${emailInvite}. Un lien lui a été envoyé pour choisir son mot de passe.`
          : `Invitation envoyée à ${emailInvite}.`,
      );
      setEmailInvite('');
      setPrenomInvite('');
      setNomInvite('');
      await recharger();
    } catch (e) {
      setErreur(messageErreur(e));
    } finally {
      setEnvoiEnCours(false);
    }
  }

  async function renommer(membre: Membre, prenom: string, nomFamille: string) {
    await api.requete(`/tenant/members/${membre.id}/identity`, {
      methode: 'PATCH',
      corps: { firstName: prenom, lastName: nomFamille },
    });
    await recharger();
  }

  async function changerRole(membre: Membre, role: Membre['role']) {
    setErreur(null);
    try {
      await api.requete(`/tenant/members/${membre.id}`, {
        methode: 'PATCH',
        corps: { role },
      });
      await recharger();
    } catch (e) {
      setErreur(messageErreur(e));
    }
  }

  async function retirer(membre: Membre) {
    // Confirmation explicite : le retrait coupe l'accès immédiatement.
    const ok = await confirmer({
      titre: 'Retirer ce membre ?',
      message: `${membre.displayName} perdra l'accès à cet espace immédiatement. `
        + 'Son compte et son travail ne sont pas supprimés.',
      libelleConfirmation: 'Retirer',
      destructeur: true,
    });
    if (!ok) return;
    setErreur(null);
    try {
      await api.requete(`/tenant/members/${membre.id}`, { methode: 'DELETE' });
      notifier(`${membre.email} n'a plus accès à l'espace.`);
      await recharger();
    } catch (e) {
      setErreur(messageErreur(e));
    }
  }

  async function annulerInvitation(invitation: InvitationEnAttente) {
    setErreur(null);
    try {
      await api.requete(`/tenant/invitations/${invitation.id}`, { methode: 'DELETE' });
      await recharger();
    } catch (e) {
      setErreur(messageErreur(e));
    }
  }

  if (erreur && !membres) return <Message ton="erreur">{erreur}</Message>;

  /*
   * Offre sans gestion d'équipe : on dit pourquoi, on ne fait pas semblant.
   *
   * L'entrée de menu disparaît, mais un signet ou un lien partagé mène encore
   * ici. Afficher l'écran complet laisserait saisir une adresse, choisir un
   * rôle, cliquer — pour un refus de quota après coup. Une page qui explique
   * et renvoie vers les offres respecte davantage le temps du lecteur.
   */
  if (!equipeIncluse) {
    return (
      <>
        <EnTetePage titre="Équipe" />
        <Carte>
          <Vide
            icone={<IconeEquipe taille={28} />}
            titre="Votre offre couvre un seul compte"
          >
            La gestion des comptes d'une équipe fait partie de l'offre École :
            le directeur y achète le nombre de comptes nécessaires et les
            administre lui-même.
            <span className="mt-4 block">
              <LienBouton vers="/abonnement" variante="primary">
                Voir les offres
              </LienBouton>
            </span>
          </Vide>
        </Carte>
      </>
    );
  }

  if (!membres || !moi) {
    return (
      <>
        <EnTetePage titre="Équipe" />
        <Carte titre="Membres"><SqueletteTableau colonnes={3} /></Carte>
      </>
    );
  }

  const administrateur = RANG[moi.role] >= RANG.ADMIN;
  const nombreProprietaires = membres.filter((m) => m.role === 'OWNER').length;
  const enAttente = membres.filter((m) => !m.acceptedAt).length;

  return (
    <>
      {/* L'en-tête ouvre la page. Placé après le message d'erreur, il faisait
          apparaître le titre en deuxième position le jour où quelque chose
          échouait — le seul jour où l'on a besoin de savoir où l'on est. */}
      <EnTetePage
        titre="Équipe"
        contexte={
          `${moi.tenantName} · ${membres.length} membre${membres.length > 1 ? 's' : ''}`
          + (enAttente > 0 ? ` · ${enAttente} en attente d'acceptation` : '')
        }
      />

      <div className="flex flex-col gap-4">
        {erreur && <Message ton="erreur">{erreur}</Message>}

        <Carte
          titre="Membres"
          description="Qui accède à cet espace, et avec quels droits."
        >
          <Tableau
            redimensionnable="membres"
            legende={`Membres de ${moi.tenantName}, avec leur rôle`}
            lignes={membres}
            cleLigne={(m) => m.id}
            colonnes={[
              {
                cle: 'personne',
                entete: 'Membre',
                /*
                 * Le NOM en tête, l'adresse en dessous.
                 *
                 * On confie une classe à quelqu'un, pas à une adresse. La liste
                 * ne portait que des adresses : dans une école de huit, deux
                 * « c.martin@ » et « c.martin1@ » ne se distinguent pas, et la
                 * CM1 atterrissait chez le mauvais collègue.
                 *
                 * L'adresse reste : c'est l'identifiant de connexion, et c'est
                 * ce qu'on relit quand on cherche pourquoi quelqu'un n'a rien
                 * reçu.
                 */
                cellule: (m) => (
                  <span className="flex flex-col gap-0.5">
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">{m.displayName}</span>
                      {m.userId === moi.id && (
                        <span className="text-xs text-[var(--color-fg-muted)]">(vous)</span>
                      )}
                      {!m.acceptedAt && (
                        <Etiquette ton="attention">Invitation non acceptée</Etiquette>
                      )}
                      {/* Au-delà des sièges payés : la direction voit QUI
                          sera touché, avant que ce soit le cas. */}
                      {m.siege === 'LECTURE_SEULE' && (
                        <Etiquette ton="erreur">Lecture seule (hors sièges)</Etiquette>
                      )}
                      {m.siege === 'A_ECHEANCE' && (
                        <Etiquette ton="attention">Hors sièges au renouvellement</Etiquette>
                      )}
                    </span>
                    {/* Tu quand il n'y a pas de nom : l'adresse est déjà
                        au-dessus, et la répéter ferait deux fois la même
                        ligne. */}
                    {m.displayName !== m.email && (
                      <span className="text-[13px] text-[var(--color-fg-muted)]">
                        {m.email}
                      </span>
                    )}
                  </span>
                ),
              },
              {
                cle: 'role',
                entete: 'Rôle',
                largeur: '12rem',
                cellule: (m) => {
                  const soiMeme = m.userId === moi.id;
                  const dernierProprietaire = m.role === 'OWNER' && nombreProprietaires === 1;
                  // Le serveur refuse déjà d'agir sur un rang supérieur : on ne
                  // propose donc pas l'action, plutôt que d'exposer un échec.
                  const modifiable = administrateur && !soiMeme && RANG[m.role] <= RANG[moi.role];

                  if (!modifiable || dernierProprietaire) return LIBELLE_ROLE[m.role];
                  return (
                    <Selecteur
                      libelle={`Rôle de ${m.email}`}
                      libelleMasque
                      value={m.role}
                      onChange={(e) => void changerRole(m, e.target.value as Membre['role'])}
                      options={ROLES_ASSIGNABLES
                        // On ne distribue pas un rôle supérieur au sien.
                        .filter((r) => RANG[r] <= RANG[moi.role])
                        .map((r) => ({ valeur: r, texte: LIBELLE_ROLE[r] }))}
                    />
                  );
                },
              },
              ...(administrateur ? [{
                cle: 'actions',
                entete: 'Actions',
                actions: true,
                largeur: '1%',
                cellule: (m: Membre) => {
                  const soiMeme = m.userId === moi.id;
                  const dernierProprietaire = m.role === 'OWNER' && nombreProprietaires === 1;
                  const retirable = administrateur && !soiMeme && RANG[m.role] <= RANG[moi.role];
                  /*
                   * Renommer n'est PAS retirer.
                   *
                   * On corrige sa propre fiche comme celle d'un collègue —
                   * c'est même le cas courant, le directeur qui a saisi
                   * « Clair » au lieu de « Claire ». Et la protection du
                   * dernier propriétaire ne concerne que le retrait : elle
                   * n'a aucune raison d'empêcher de corriger un prénom.
                   */
                  const renommable = administrateur && RANG[m.role] <= RANG[moi.role];

                  if (!renommable && !retirable) {
                    return dernierProprietaire ? (
                      <span className="text-xs text-[var(--color-fg-muted)]">
                        Dernier propriétaire
                      </span>
                    ) : null;
                  }
                  return (
                    <ActionsLigne>
                      {/* La mention reste, à côté du crayon : c'est elle qui
                          explique l'absence de corbeille. La retirer ferait
                          croire à un oubli d'affichage. */}
                      {dernierProprietaire && (
                        <span className="text-xs text-[var(--color-fg-muted)]">
                          Dernier propriétaire
                        </span>
                      )}
                      {renommable && (
                        <Bouton
                          variante="ghost"
                          taille="sm"
                          iconeSeule
                          icone={<IconeCrayon taille={16} />}
                          onClick={() => setRenomme(m)}
                          aria-label={`Modifier le nom de ${m.displayName}`}
                        >
                          Modifier le nom
                        </Bouton>
                      )}
                      {retirable && !dernierProprietaire && (
                        <Bouton
                          variante="ghost"
                          taille="sm"
                          iconeSeule
                          icone={<IconeCorbeille taille={16} />}
                          onClick={() => void retirer(m)}
                          // Plusieurs boutons « Retirer » dans un tableau :
                          // listés hors contexte par un lecteur d'écran, ils
                          // seraient indiscernables sans ce libellé.
                          aria-label={`Retirer ${m.displayName}`}
                        >
                          Retirer
                        </Bouton>
                      )}
                    </ActionsLigne>
                  );
                },
              }] : []),
            ]}
          />
        </Carte>

        <GestionClasses membres={membres} estResponsable={administrateur} />

        {/* Le dialogue vit hors du tableau : y placer un dialogue par ligne en
            monterait autant qu'il y a de membres, tous fermés. */}
        <DialogueNomMembre
          membre={renomme}
          surFermeture={() => setRenomme(null)}
          surEnregistrement={renommer}
        />

        {administrateur && (
          <Carte
            titre="Ajouter un collègue"
            description={
              mode === 'creation'
                ? 'Le compte est créé immédiatement et vous pouvez lui confier une classe. Votre collègue choisit son mot de passe par le lien reçu.'
                : 'Un courriel lui est envoyé ; le compte n’existera qu’une fois l’invitation acceptée.'
            }
            action={
              /*
               * Deux façons d'ajouter, et le choix compte.
               *
               * La CRÉATION fait exister le compte tout de suite : c'est ce
               * qu'on veut à la rentrée, pour répartir les classes avant que
               * chacun se connecte. L'INVITATION laisse l'autre décider
               * d'entrer — la seule voie possible pour une adresse qui a déjà
               * un compte ailleurs.
               *
               * Dans les deux cas, le mot de passe est choisi par son
               * titulaire : personne d'autre ne le connaît, pas même la
               * direction.
               */
              <GroupeBoutons
                legende="Méthode"
                valeur={mode}
                surChangement={setMode}
                options={[
                  { valeur: 'creation', texte: 'Créer le compte' },
                  { valeur: 'invitation', texte: 'Inviter' },
                ]}
              />
            }
          >
            <form onSubmit={inviter} className="flex flex-col gap-4">
              {/* Adresse et rôle sur une même ligne au-delà de 640 px : ils se
                  décident ensemble, et deux champs empilés sur toute la largeur
                  d'un écran d'ordinateur donnent un formulaire dégingandé. */}
              {/*
                * Le nom, seulement pour une CRÉATION.
                *
                * C'est la direction qui répartit les classes, souvent avant que
                * quiconque se soit connecté : une liste d'équipe faite
                * d'adresses ne permet pas de confier la CM1 à la bonne
                * personne. Une invitation, elle, laisse l'intéressé se nommer.
                */}
              {mode === 'creation' && (
                <GrilleFormulaire>
                  <Champ
                    libelle="Prénom"
                    value={prenomInvite}
                    onChange={(e) => setPrenomInvite(e.target.value)}
                    autoComplete="off"
                    maxLength={60}
                    required
                  />
                  <Champ
                    libelle="Nom"
                    value={nomInvite}
                    onChange={(e) => setNomInvite(e.target.value)}
                    autoComplete="off"
                    maxLength={60}
                    required
                  />
                </GrilleFormulaire>
              )}
              <GrilleFormulaire>
                <Champ
                  libelle="Adresse électronique"
                  type="email"
                  value={emailInvite}
                  onChange={(e) => setEmailInvite(e.target.value)}
                  autoComplete="off"
                  required
                />
                {/* `Selecteur`, et non un <select> rhabillé sur place : celui
                    qui était écrit ici avait sa propre hauteur, sa propre
                    bordure et son propre anneau de focus — deux champs voisins
                    qui ne s'alignaient pas. */}
                <Selecteur
                  libelle="Rôle"
                  value={roleInvite}
                  onChange={(e) => setRoleInvite(e.target.value as Membre['role'])}
                  options={ROLES_ASSIGNABLES
                    .filter((r) => RANG[r] <= RANG[moi.role])
                    .map((r) => ({ valeur: r, texte: LIBELLE_ROLE[r] }))}
                />
              </GrilleFormulaire>
              <div>
                <Bouton type="submit" variante="primary" chargement={envoiEnCours}>
                  {mode === 'creation' ? 'Créer le compte' : 'Envoyer l’invitation'}
                </Bouton>
              </div>
            </form>
          </Carte>
        )}

        {administrateur && (
          <Carte titre="Invitations en attente">
            {invitations.length === 0 ? (
              <Vide
                icone={<IconeEquipe taille={28} />}
                titre="Aucune invitation en attente"
              >
                Les invitations envoyées apparaissent ici jusqu'à leur
                acceptation.
              </Vide>
            ) : (
              // Tableau plutôt que liste : trois informations par ligne, que
              // l'œil ne compare d'une ligne à l'autre que si elles sont en
              // colonnes alignées.
              <Tableau
            redimensionnable="invitations"
                legende="Invitations envoyées et non encore acceptées"
                lignes={invitations}
                cleLigne={(i) => i.id}
                colonnes={[
                  {
                    cle: 'email',
                    entete: 'Adresse',
                    cellule: (i) => <span className="font-medium">{i.email}</span>,
                  },
                  {
                    cle: 'role',
                    entete: 'Rôle',
                    largeur: '10rem',
                    cellule: (i) => LIBELLE_ROLE[i.role],
                  },
                  {
                    cle: 'expiration',
                    entete: 'Expire le',
                    largeur: '9rem',
                    cellule: (i) => new Date(i.expiresAt).toLocaleDateString('fr-FR'),
                  },
                  {
                    cle: 'actions',
                    entete: 'Actions',
                    actions: true,
                    largeur: '1%',
                    cellule: (i) => (
                      <ActionsLigne>
                        <Bouton
                          variante="ghost"
                          taille="sm"
                          iconeSeule
                          icone={<IconeCroix taille={16} />}
                          onClick={() => void annulerInvitation(i)}
                          aria-label={`Annuler l'invitation de ${i.email}`}
                        >
                          Annuler
                        </Bouton>
                      </ActionsLigne>
                    ),
                  },
                ]}
              />
            )}
          </Carte>
        )}
      </div>
    </>
  );
}
